<template>
  <div>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <!-- Header mit Titel und Icon -->
      <div class="flex justify-between items-center mb-6 bg-white rounded-xl p-5">
        <div>
          <h1 class="text-xl font-bold text-gray-800 mb-1">Schadensdokumentation</h1>
          <p class="text-sm text-gray-600">Bitte dokumentiere alle Schäden am Fahrzeug</p>
        </div>
        <div>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-[#E9041D] opacity-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-sm overflow-hidden">
        <div class="p-5">
          <!-- Ladezustand -->
          <div v-if="isLoading" class="py-8 flex flex-col items-center justify-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E9041D] mb-4"></div>
            <p class="text-gray-600">Lade Schadenskatalog...</p>
          </div>

          <!-- Fehlerzustand -->
          <div v-else-if="error" class="py-8 flex flex-col items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p class="text-gray-600">{{ error }}</p>
            <button
              @click="router.push('/dashboard')"
              class="mt-4 px-4 py-2 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Zurück zum Dashboard
            </button>
          </div>

          <!-- Hauptinhalt -->
          <div v-else>
            <!-- Bereits bekannte Schäden -->
            <div v-if="existingDamages.length > 0" class="mb-6">
              <h2 class="text-base font-medium text-gray-800 mb-3">Bereits bekannte Schäden</h2>
              <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                <div class="flex items-center mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p class="text-sm font-medium text-red-800">
                    {{ vehicleInfo?.license_plate }} - {{ vehicleInfo?.brand }} {{ vehicleInfo?.model }}
                  </p>
                </div>
                <div class="space-y-3">
                  <div v-for="damage in existingDamages" :key="damage.id" class="border border-red-200 rounded-lg p-3 bg-white">
                    <div class="flex justify-between items-start">
                      <div>
                        <h3 class="font-medium text-gray-800 text-sm">{{ damage.damage_category.name }}</h3>
                        <p v-if="damage.description" class="text-xs text-gray-600 mt-1">{{ damage.description }}</p>
                        <p class="text-xs text-red-600 mt-1">
                          Dokumentiert am {{ formatDate(damage.created_at) }}
                        </p>
                        <p class="text-xs text-gray-500 mt-1">
                          Check-In: {{ damage.check_in.checkin_location.name }} ({{ formatDate(damage.check_in.created_at) }})
                        </p>
                      </div>
                    </div>
                    <div v-if="damageImageUrls(damage).length" class="mt-3 flex gap-2 overflow-x-auto">
                      <img v-for="(url, i) in damageImageUrls(damage)" :key="i" :src="url" alt="Schadenfoto" class="h-20 w-auto object-cover rounded-md flex-shrink-0">
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Keine bekannten Schäden -->
            <div v-else-if="vehicleInfo && existingDamages.length === 0" class="mb-6">
              <h2 class="text-base font-medium text-gray-800 mb-3">Bereits bekannte Schäden</h2>
              <div class="bg-green-50 border border-green-200 rounded-lg p-4">
                <div class="flex items-center mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p class="text-sm font-medium text-green-800">
                    {{ vehicleInfo?.license_plate }} - {{ vehicleInfo?.brand }} {{ vehicleInfo?.model }}
                  </p>
                </div>
                <p class="text-sm text-green-700">Keine bekannten Schäden für dieses Fahrzeug gefunden.</p>
              </div>
            </div>

            <!-- Dokumentierte Schäden -->
            <div v-if="documentedDamages.length > 0" class="mb-6">
              <h2 class="text-base font-medium text-gray-800 mb-3">Dokumentierte Schäden</h2>
              <div class="space-y-3">
                <div v-for="(damage, index) in documentedDamages" :key="damage.id || index" class="border border-gray-200 rounded-lg p-4">
                  <div class="flex justify-between items-start">
                    <div>
                      <h3 class="font-medium text-gray-800 text-sm">{{ getDamagePath(damage) }}</h3>
                      <p v-if="damage.description" class="text-xs text-gray-600 mt-1">{{ damage.description }}</p>
                    </div>
                    <button
                      @click="removeDamage(index)"
                      :disabled="isDeletingDamage"
                      class="text-gray-400 hover:text-red-500 disabled:opacity-50"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div v-if="damage.image_urls.length" class="mt-3 flex gap-2 overflow-x-auto">
                    <img v-for="(url, i) in damage.image_urls" :key="i" :src="url" alt="Schadenfoto" class="h-20 w-auto object-cover rounded-md flex-shrink-0">
                  </div>
                  <button
                    v-if="damage.id"
                    @click="addPhotoToExisting(damage)"
                    :disabled="addingPhotoToDamageId !== null"
                    class="mt-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:border-[#E9041D] hover:text-[#E9041D] transition-colors disabled:opacity-50"
                  >
                    {{ addingPhotoToDamageId === damage.id ? 'Wird hochgeladen...' : '+ Foto hinzufügen' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Schaden erfassen Button -->
            <div class="mb-6">
              <button
                @click="startDamageCapture"
                class="w-full px-4 py-4 bg-white border-2 border-[#E9041D] text-[#E9041D] rounded-lg hover:bg-red-50 transition-colors text-base font-medium flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Schaden erfassen
              </button>
            </div>

            <!-- Schaden erfassen Modal -->
            <div v-if="showDamageModal" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
              <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-hidden">
                <div class="p-4 border-b border-gray-200 flex justify-between items-center">
                  <h2 class="text-lg font-medium text-gray-800">Schaden erfassen</h2>
                  <button @click="closeDamageModal" class="text-gray-400 hover:text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div class="p-4 overflow-y-auto max-h-[calc(90vh-8rem)]">
                  <!-- Kategorie-Navigation -->
                  <div class="mb-4">
                    <div class="flex items-center space-x-2 text-sm text-gray-500 mb-3 overflow-x-auto py-1 px-1">
                      <button
                        v-for="(item, index) in selectedPath"
                        :key="index"
                        @click="navigateToLevel(index)"
                        class="hover:text-[#E9041D] whitespace-nowrap"
                      >
                        {{ item.name }}
                      </button>
                      <span v-if="selectedPath.length > 0" class="text-gray-400">/</span>
                      <span v-if="selectedPath.length > 0" class="text-gray-400">Schaden auswählen</span>
                    </div>

                    <!-- Aktuelle Kategorie -->
                    <div v-if="currentItems.length > 0" class="grid grid-cols-1 gap-2">
                      <button
                        v-for="item in currentItems"
                        :key="item.id"
                        @click="selectItem(item)"
                        class="p-3 border border-gray-200 rounded-lg hover:border-[#E9041D] hover:bg-red-50 transition-colors text-left"
                      >
                        <div class="font-medium text-gray-800 text-sm">{{ item.name }}</div>
                        <div v-if="item.description" class="text-xs text-gray-500 mt-1">{{ item.description }}</div>
                      </button>
                    </div>

                    <!-- Keine weiteren Unterkategorien -->
                    <div v-else-if="selectedPath.length > 0" class="text-center py-4">
                      <button
                        @click="capturePhoto"
                        class="w-full px-4 py-3 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                      >
                        Foto aufnehmen
                      </button>
                    </div>

                    <!-- Startkategorien -->
                    <div v-else class="grid grid-cols-1 gap-2">
                      <button
                        v-for="(_, category) in damageCategories"
                        :key="category"
                        @click="selectCategory(category)"
                        class="p-3 border border-gray-200 rounded-lg hover:border-[#E9041D] hover:bg-red-50 transition-colors text-left"
                      >
                        <div class="font-medium text-gray-800 text-sm">{{ getCategoryName(category) }}</div>
                      </button>
                    </div>
                  </div>

                  <!-- Foto und Beschreibung -->
                  <div v-if="currentDamage.photos.length > 0 || currentDamage.description" class="border-t border-gray-200 pt-4">
                    <h3 class="text-base font-medium text-gray-800 mb-3">Schaden dokumentieren</h3>

                    <!-- Fotos (DEV-200: beliebig viele pro Schaden) -->
                    <div v-if="currentDamage.photos.length > 0" class="mb-4">
                      <label class="block text-sm font-medium text-gray-700 mb-2">
                        {{ currentDamage.photos.length }} Foto{{ currentDamage.photos.length !== 1 ? 's' : '' }}
                      </label>
                      <div class="grid grid-cols-3 gap-2">
                        <div v-for="(photo, index) in currentDamage.photos" :key="index" class="relative">
                          <img :src="photo" alt="Schadenfoto" class="h-24 w-full object-cover rounded-md">
                          <button
                            @click="removeCapturedPhoto(index)"
                            class="absolute -top-1.5 -right-1.5 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs shadow"
                          >
                            ✕
                          </button>
                        </div>
                      </div>

                      <!-- Ralfs Ja/Nein-Loop: nach jedem Foto fragen (DEV-200) -->
                      <div v-if="showAddMorePrompt" class="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <p class="text-sm font-medium text-gray-800 mb-2">Bild hinzufügen?</p>
                        <div class="flex gap-2">
                          <button
                            @click="capturePhoto"
                            class="flex-1 px-3 py-2 bg-[#E9041D] text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                          >
                            Ja, noch ein Foto
                          </button>
                          <button
                            @click="showAddMorePrompt = false"
                            class="flex-1 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                          >
                            Nein, weiter
                          </button>
                        </div>
                      </div>
                      <button
                        v-else
                        @click="capturePhoto"
                        class="mt-3 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:border-[#E9041D] hover:text-[#E9041D] transition-colors"
                      >
                        + weiteres Foto
                      </button>
                    </div>

                    <!-- Beschreibung -->
                    <div class="mb-4">
                      <label for="description" class="block text-sm font-medium text-gray-700 mb-1">
                        Beschreibung <span class="text-red-500">*</span>
                      </label>
                      <textarea
                        id="description"
                        v-model="currentDamage.description"
                        rows="3"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#E9041D] focus:border-[#E9041D] focus:outline-none text-sm"
                        placeholder="Beschreibe den Schaden..."
                        required
                      ></textarea>
                      <p v-if="!currentDamage.description" class="mt-1 text-sm text-red-500">
                        Bitte geben Sie eine Beschreibung des Schadens ein
                      </p>
                    </div>

                    <!-- Speichern Button -->
                    <div class="flex justify-end">
                      <button
                        @click="saveCurrentDamage"
                        :disabled="!currentDamage.description || isSavingDamage"
                        class="w-full px-4 py-3 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span v-if="isSavingDamage">Wird gespeichert...</span>
                        <span v-else>Schaden speichern</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Navigation Buttons -->
            <div class="mt-6 space-y-3">
              <button
                @click="finishDamageDocumentation"
                class="w-full px-4 py-4 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors text-base font-medium"
              >
                Weiter
              </button>
              <button
                type="button"
                @click="showCancelDialog = true"
                :disabled="isCancelling"
                class="w-full py-3 px-4 bg-white text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Check-In abbrechen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cancel Confirmation Dialog -->
    <div v-if="showCancelDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
        <h3 class="text-lg font-bold text-gray-800 mb-2">Check-In abbrechen?</h3>
        <p class="text-sm text-gray-600 mb-6">Der Check-In wird unwiderruflich gelöscht. Alle bisherigen Eingaben und dokumentierte Schäden gehen verloren.</p>
        <div class="flex space-x-3">
          <button
            @click="showCancelDialog = false"
            class="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Zurück
          </button>
          <button
            @click="cancelCheckin(checkinIdNumber)"
            :disabled="isCancelling"
            class="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
          >
            {{ isCancelling ? 'Wird gelöscht...' : 'Abbrechen' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router'
import api from '../api/axios'
import { useCheckinHeartbeat } from '../composables/useCheckinHeartbeat'
import { useCancelCheckin } from '../composables/useCancelCheckin'
import { useCameraCapture } from '../composables/useCameraCapture'

interface DamageItem {
  id: number
  name: string
  type: string
  parent_id: number | null
  sort_order: number
  is_active: boolean
  severity: string
  created_at: string
  updated_at: string
  description?: string
  children?: DamageItem[]
}

interface DamageCategories {
  interior: DamageItem[]
  exterior: DamageItem[]
  other: DamageItem[]
}

interface DocumentedDamage {
  id?: number
  path: DamageItem[]
  description: string
  image_urls: string[]
  damage_category_id?: number
}

// Local capture state before the damage is saved (DEV-200: multiple photos).
interface CurrentDamage {
  path: DamageItem[]
  description: string
  photos: string[]
}

interface ExistingDamage {
  id: number
  damage_category_id: number
  description: string
  image: string | null
  image_url?: string | null
  created_at: string
  damage_category: {
    id: number
    name: string
  }
  check_in: {
    id: number
    created_at: string
    checkin_location: {
      id: number
      name: string
    }
  }
}

interface CheckIn {
  id: number
  created_at: string
  damages: ExistingDamage[]
}

interface Vehicle {
  id: number
  license_plate: string
  brand: string
  model: string
  check_ins: CheckIn[]
}

const router = useRouter()
const route = useRoute()
const checkId = computed(() => route.params.id as string)
const checkinIdNumber = computed(() => parseInt(checkId.value))

// Heartbeat for connection monitoring
const {
  startHeartbeat,
  stopHeartbeat
} = useCheckinHeartbeat(checkinIdNumber)

const { isCancelling, showCancelDialog, cancelCheckin } = useCancelCheckin(stopHeartbeat)

const isLoading = ref(true)
const error = ref<string | null>(null)
const isSavingDamage = ref(false)
const isDeletingDamage = ref(false)
const damageCategories = ref<DamageCategories>({
  interior: [],
  exterior: [],
  other: []
})

// Schadensdokumentation
const documentedDamages = ref<DocumentedDamage[]>([])
const existingDamages = ref<ExistingDamage[]>([])
const vehicleInfo = ref<Vehicle | null>(null)
const showDamageModal = ref(false)
const selectedPath = ref<DamageItem[]>([])
const currentItems = ref<DamageItem[]>([])
const currentDamage = ref<CurrentDamage>({
  path: [],
  description: '',
  photos: []
})

// DEV-200: after each captured photo, ask "Bild hinzufügen? Ja / Nein".
const showAddMorePrompt = ref(false)
// Damage id currently receiving an after-the-fact photo (spinner state).
const addingPhotoToDamageId = ref<number | null>(null)

// Display URLs for a damage from the API: images[] (multi) with fallback to
// the legacy single image_url. DEV-200.
const damageImageUrls = (d: any): string[] => {
  if (d?.images?.length) return d.images.map((i: any) => i.image_url).filter(Boolean)
  return d?.image_url ? [d.image_url] : []
}

// Navigation guard — warn if modal is open with unsaved content
onBeforeRouteLeave((_to, _from, next) => {
  if (showDamageModal.value && (currentDamage.value.photos.length > 0 || currentDamage.value.description)) {
    const leave = window.confirm('Du hast einen ungespeicherten Schaden. Möchtest du die Seite wirklich verlassen?')
    if (!leave) {
      next(false)
      return
    }
  }
  next()
})

// beforeunload listener — prevent browser refresh/close with unsaved modal content
const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
  if (showDamageModal.value && (currentDamage.value.photos.length > 0 || currentDamage.value.description)) {
    e.preventDefault()
  }
}

// Load saved damages from server on mount
const loadSavedDamages = async () => {
  try {
    const response = await api.get(`/api/checker/checkins/${checkId.value}`)
    const checkinData = response.data.data

    if (checkinData.damages && checkinData.damages.length > 0) {
      documentedDamages.value = checkinData.damages.map((d: any) => ({
        id: d.id,
        path: d.damage_category ? [{ id: d.damage_category.id, name: d.damage_category.name } as DamageItem] : [],
        description: d.description || '',
        image_urls: damageImageUrls(d),
        damage_category_id: d.damage_category_id
      }))
    }
  } catch (err: any) {
    console.error('Fehler beim Laden gespeicherter Schäden:', err)
    // Retry once after a short delay (token may not be ready yet after re-login)
    try {
      await new Promise(resolve => setTimeout(resolve, 1000))
      const retryResponse = await api.get(`/api/checker/checkins/${checkId.value}`)
      const retryData = retryResponse.data.data

      if (retryData.damages && retryData.damages.length > 0) {
        documentedDamages.value = retryData.damages.map((d: any) => ({
          id: d.id,
          path: d.damage_category ? [{ id: d.damage_category.id, name: d.damage_category.name } as DamageItem] : [],
          description: d.description || '',
          image_urls: damageImageUrls(d),
          damage_category_id: d.damage_category_id
        }))
      }
    } catch (retryErr: any) {
      console.error('Retry fehlgeschlagen:', retryErr)
    }
  }
}

// Lade Schadenskatalog
const fetchDamageCatalog = async () => {
  isLoading.value = true
  error.value = null

  try {
    const response = await api.get('/api/damage-categories')
    const rawData = response.data.data

    // Gruppiere die Daten nach Typ
    const groupedData: DamageCategories = {
      interior: [],
      exterior: [],
      other: []
    }

    rawData.forEach((item: DamageItem) => {
      if (item.type === 'interior') {
        groupedData.interior.push(item)
      } else if (item.type === 'exterior') {
        groupedData.exterior.push(item)
      } else {
        groupedData.other.push(item)
      }
    })

    damageCategories.value = groupedData

    // Verarbeite die Kategorien, um die Hierarchie aufzubauen
    processCategories()
  } catch (err: any) {
    error.value = 'Der Schadenskatalog konnte nicht geladen werden. Bitte versuche es später erneut.'
  } finally {
    isLoading.value = false
  }
}

// Lade Vehicle-Daten und bereits bekannte Schäden
const fetchVehicleData = async () => {
  try {
    const checkInResponse = await api.get(`/api/checker/checkins/${checkId.value}`)
    const licensePlate = checkInResponse.data.data.vehicle.license_plate

    const searchResponse = await api.post('/api/vehicles/search', {
      license_plate: licensePlate
    })

    if (searchResponse.data.data && searchResponse.data.data.length > 0) {
      const vehicleId = searchResponse.data.data[0].id

      const damagesResponse = await api.get(`/api/vehicles/${vehicleId}/damages`)
      const responseData = damagesResponse.data.data || damagesResponse.data

      vehicleInfo.value = {
        id: responseData.vehicle.id,
        license_plate: responseData.vehicle.license_plate,
        brand: responseData.vehicle.brand,
        model: responseData.vehicle.model,
        check_ins: []
      }

      existingDamages.value = responseData.damages
    }

  } catch (err: any) {
    // Nicht als kritischen Fehler behandeln
  }
}

// Verarbeite die Kategorien, um die Hierarchie aufzubauen
const processCategories = () => {
  const buildHierarchy = (items: DamageItem[]): DamageItem[] => {
    const itemsMap = new Map<number, DamageItem>()
    items.forEach(item => {
      itemsMap.set(item.id, { ...item, children: [] })
    })

    const rootItems: DamageItem[] = []
    items.forEach(item => {
      const processedItem = itemsMap.get(item.id)!
      if (item.parent_id === null) {
        rootItems.push(processedItem)
      } else {
        const parent = itemsMap.get(item.parent_id)
        if (parent) {
          if (!parent.children) {
            parent.children = []
          }
          parent.children.push(processedItem)
        }
      }
    })

    const sortItems = (items: DamageItem[]) => {
      items.sort((a, b) => a.sort_order - b.sort_order)
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          sortItems(item.children)
        }
      })
    }

    sortItems(rootItems)
    return rootItems
  }

  const categories = ['interior', 'exterior', 'other'] as const
  categories.forEach(category => {
    const items = damageCategories.value[category]
    damageCategories.value[category] = buildHierarchy(items)
  })
}

// Kategorienamen anzeigen
const getCategoryName = (category: string): string => {
  const names: Record<string, string> = {
    interior: 'Innenraum',
    exterior: 'Außenbereich',
    other: 'Sonstige'
  }
  return names[category] || category
}

// Schaden erfassen starten
const startDamageCapture = () => {
  selectedPath.value = []
  currentItems.value = []
  currentDamage.value = {
    path: [],
    description: '',
    photos: []
  }
  showAddMorePrompt.value = false
  showDamageModal.value = true
}

// Modal schließen
const closeDamageModal = () => {
  showDamageModal.value = false
}

// Kategorie auswählen
const selectCategory = (category: string) => {
  const items = damageCategories.value[category as keyof DamageCategories]
  currentItems.value = items.filter(item => item.is_active && !item.parent_id)
  selectedPath.value = []
}

// Item auswählen
const selectItem = (item: DamageItem) => {
  selectedPath.value.push(item)
  currentDamage.value.path = [...selectedPath.value]

  if (item.children && item.children.length > 0) {
    currentItems.value = item.children.filter(child => child.is_active)
  } else {
    currentItems.value = []
  }
}

// Zur bestimmten Ebene navigieren
const navigateToLevel = (level: number) => {
  selectedPath.value = selectedPath.value.slice(0, level + 1)
  currentDamage.value.path = [...selectedPath.value]

  if (level === -1) {
    currentItems.value = []
  } else {
    const item = selectedPath.value[level]
    if (item.children && item.children.length > 0) {
      currentItems.value = item.children.filter(child => child.is_active)
    } else {
      currentItems.value = []
    }
  }
}

// Kamera öffnen und EIN Foto liefern (oder null bei Abbruch). DEV-200: von
// capturePhoto (Erfassung) und addPhotoToExisting (Nachtrag) gemeinsam genutzt.
// DEV-216: konsolidiert auf useCameraCapture (Fehler-Mapping + nativer
// Fallback); cameraError wird in das View-eigene error-Banner gespiegelt.
const { openCamera: openCameraCapture, cameraError } = useCameraCapture()

const openCamera = async (): Promise<string | null> => {
  const photo = await openCameraCapture()
  if (cameraError.value) {
    error.value = cameraError.value
  }
  return photo
}

// Foto zur laufenden Erfassung aufnehmen — danach fragt der Prompt „Bild
// hinzufügen?" nach weiteren Fotos (Ralfs Ja/Nein-Loop, DEV-200).
const capturePhoto = async () => {
  showAddMorePrompt.value = false
  const photo = await openCamera()
  if (photo) {
    currentDamage.value.photos.push(photo)
    showAddMorePrompt.value = true
  }
}

const removeCapturedPhoto = (index: number) => {
  currentDamage.value.photos.splice(index, 1)
}

// Nachträglich ein Foto zu einem bereits gespeicherten Schaden aufnehmen und
// hochladen (POST /api/vehicle-damages/{id}/images). DEV-200.
const addPhotoToExisting = async (damage: DocumentedDamage) => {
  if (!damage.id || addingPhotoToDamageId.value !== null) return

  const photo = await openCamera()
  if (!photo) return

  addingPhotoToDamageId.value = damage.id
  try {
    const blob = await (await fetch(photo)).blob()
    const formData = new FormData()
    formData.append('images[]', blob, 'damage.jpg')

    const result = await api.post(`/api/vehicle-damages/${damage.id}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    damage.image_urls = damageImageUrls(result.data.data)
  } catch (err: any) {
    error.value = err.response?.data?.message || 'Das Foto konnte nicht hochgeladen werden.'
  } finally {
    addingPhotoToDamageId.value = null
  }
}

// Aktuellen Schaden speichern — POST immediately to server
const saveCurrentDamage = async () => {
  if (!currentDamage.value.description || isSavingDamage.value) {
    return
  }

  isSavingDamage.value = true

  try {
    const formData = new FormData()
    formData.append('vehicle_checkin_id', checkId.value)
    formData.append('damage_category_id', String(currentDamage.value.path[currentDamage.value.path.length - 1].id))
    formData.append('description', currentDamage.value.description)

    // DEV-200: all captured photos ride as images[] into vehicle_damage_images.
    for (const [index, photo] of currentDamage.value.photos.entries()) {
      const response = await fetch(photo)
      const blob = await response.blob()
      formData.append('images[]', blob, `damage-${index + 1}.jpg`)
    }

    const result = await api.post('/api/vehicle-damages', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    const savedDamage = result.data.data

    documentedDamages.value.push({
      id: savedDamage.id,
      path: [...currentDamage.value.path],
      description: savedDamage.description,
      image_urls: damageImageUrls(savedDamage),
      damage_category_id: savedDamage.damage_category_id
    })

    closeDamageModal()
  } catch (err: any) {
    error.value = 'Der Schaden konnte nicht gespeichert werden. Bitte versuche es erneut.'
  } finally {
    isSavingDamage.value = false
  }
}

// Schaden entfernen — DELETE from server first
const removeDamage = async (index: number) => {
  const damage = documentedDamages.value[index]

  if (damage.id) {
    isDeletingDamage.value = true
    try {
      await api.delete(`/api/vehicle-damages/${damage.id}`)
    } catch (err: any) {
      error.value = 'Der Schaden konnte nicht gelöscht werden.'
      isDeletingDamage.value = false
      return
    }
    isDeletingDamage.value = false
  }

  documentedDamages.value.splice(index, 1)
}

// Schadenspfad anzeigen
const getDamagePath = (damage: DocumentedDamage): string => {
  return damage.path.map(item => item.name).join(' > ')
}

// Datum formatieren
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  } catch (error) {
    return dateString
  }
}

// Schadensdokumentation abschließen — just navigate, damages are already saved
const finishDamageDocumentation = () => {
  // Step 8: Zero-damage confirmation
  if (documentedDamages.value.length === 0) {
    const proceed = window.confirm('Keine Schäden dokumentiert. Fortfahren?')
    if (!proceed) {
      return
    }
  }

  router.push(`/checks/${checkId.value}/services`)
}

onMounted(async () => {
  // Load damage catalog (controls isLoading state)
  fetchDamageCatalog()
  // Load vehicle history and saved damages in parallel, await both
  await Promise.all([
    fetchVehicleData(),
    loadSavedDamages()
  ])
  window.addEventListener('beforeunload', beforeUnloadHandler)
  if (checkinIdNumber.value) {
    startHeartbeat()
  }
})

onUnmounted(() => {
  stopHeartbeat()
  window.removeEventListener('beforeunload', beforeUnloadHandler)
})
</script>
